"use client";

// ── "Running · 12 s": a run that waits its turn, said calmly (`EX-SRN01`) ──
// Wave 34 decision 12. A run that finds the judge full now waits in the run
// lane instead of being refused, so the page has one state for the whole wait:
// Running, with the seconds counting, and after a few seconds one quiet line
// saying why it takes a moment. Never the word busy. The seconds are for the
// eye only; a screen reader hears the steady label once, not a tick a second.

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

/** Seconds of waiting before the page says that runs are taking turns. */
export const RUN_IN_LINE_HINT_AFTER_S = 5;

export function RunWaitClock({
  since,
  label,
  running,
  inLine,
  className,
}: {
  /** When the person pressed Run (`Date.now()`); the clock's own first paint when unknown. */
  since?: number;
  /** The steady words a screen reader hears, "Running on the judge…". */
  label: string;
  /** The visible line for so many seconds, "Running · 12 s". */
  running: (seconds: number) => string;
  /** The quiet line shown once the wait passes {@link RUN_IN_LINE_HINT_AFTER_S}; none where a neighbour says it. */
  inLine?: string;
  className?: string;
}) {
  const [mountedAt] = useState(() => Date.now());
  const [now, setNow] = useState(mountedAt);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.floor((now - (since ?? mountedAt)) / 1_000));

  return (
    <div className={cn("font-sans text-xs", className)} data-testid="run-wait-clock">
      <p className="flex items-center gap-1.5 font-semibold text-white/80">
        <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center" aria-hidden>
          <span className="absolute inline-flex h-2 w-2 rounded-full bg-emerald-400/60 motion-safe:animate-ping" />
          <Loader2 className="h-3.5 w-3.5 text-emerald-300 motion-safe:animate-spin" />
        </span>
        <span className="sr-only">{label}</span>
        <span className="tabular-nums" aria-hidden data-testid="run-wait-seconds">
          {running(seconds)}
        </span>
      </p>
      {inLine && seconds >= RUN_IN_LINE_HINT_AFTER_S ? (
        <p className="mt-1 leading-snug text-white/50" data-testid="run-wait-in-line">
          {inLine}
        </p>
      ) : null}
    </div>
  );
}
